import React, { useState, useEffect } from "react";
import cn from "classnames";
import styles from "./CandidateList.module.sass";
import Card from "../../../../components/Card";
import Form from "../../../../components/Form";
import Table from "./Table";
import Panel from "./Panel";
import Icon from "../../../../components/Icon";

// Services
import { getOfferCandidates } from "../../../../services/offer.service";
import { showSuccess, showError } from "../../../../services/toast.service";
import eventBus, { EVENTS } from "../../../../utils/eventBus";

const CandidateList = ({ offer }) => {
    const [candidates, setCandidates] = useState([]);
    const [filteredCandidates, setFilteredCandidates] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [selectedCandidates, setSelectedCandidates] = useState([]);

    const fetchCandidates = async () => {
        if (!offer?.id) {
            setLoading(false);
            return;
        }
        
        try {
            setLoading(true);
            const candidatesResult = await getOfferCandidates(offer.id);
            
            if (!candidatesResult.success) {
                console.error('Erreur lors de la récupération des candidats:', candidatesResult.error);
                setCandidates([]);
                setFilteredCandidates([]);
                return;
            }
            
            const candidatesData = candidatesResult.data || [];
            setCandidates(candidatesData);
            setFilteredCandidates(candidatesData);
        } catch (error) {
            console.error('Erreur lors de la récupération des candidats:', error);
            setCandidates([]);
            setFilteredCandidates([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (offer) {
            fetchCandidates();
        }
    }, [offer]);

    // Écouter les événements d'ajout/suppression de candidats
    useEffect(() => {
        if (!offer?.id) return;

        const unsubscribeAdd = eventBus.on(EVENTS.PROSPECT_ADDED_TO_OFFER, (eventData) => {
            if (eventData.offerId === offer.id) {
                fetchCandidates();
            }
        });

        const unsubscribeRemove = eventBus.on(EVENTS.PROSPECT_REMOVED_FROM_OFFER, (eventData) => {
            if (eventData.offerId === offer.id) {
                fetchCandidates();
            }
        });

        return () => {
            unsubscribeAdd();
            unsubscribeRemove();
        };
    }, [offer?.id]);

    // Les candidats sont en lecture seule, pas de modal d'édition nécessaire

    const handleSelectCandidate = (candidateId, isSelected) => {
        if (isSelected) {
            setSelectedCandidates([...selectedCandidates, candidateId]);
        } else {
            setSelectedCandidates(selectedCandidates.filter(id => id !== candidateId));
        }
    };

    const handleSelectAll = (isSelected) => {
        if (isSelected) {
            const allCandidateIds = filteredCandidates.map(candidate => candidate.id);
            setSelectedCandidates(allCandidateIds);
        } else {
            setSelectedCandidates([]);
        }
    };

    const handleClearSelection = () => {
        setSelectedCandidates([]);
    };


    const filterCandidates = (searchText) => {
        if (!searchText.trim()) {
            setFilteredCandidates(candidates);
            return;
        }

        const searchLower = searchText.toLowerCase();
        const filtered = candidates.filter(candidate => {
            const fullName = `${candidate.first_name} ${candidate.last_name}`.toLowerCase();
            const email = (candidate.email || '').toLowerCase();
            const phone = (candidate.phone || '').toLowerCase();

            return fullName.includes(searchLower) ||
                email.includes(searchLower) ||
                phone.includes(searchLower);
        });

        setFilteredCandidates(filtered);
    };

    useEffect(() => {
        filterCandidates(searchTerm);
    }, [searchTerm, candidates]);

    return (
        <>
            <Card
                className={styles.card}
                classCardHead={styles.head}
                title="Candidat(e)s"
                classTitle={cn("title-blue", styles.title)}
                head={
                    <>
                      <Form
                        className={styles.form}
                        value={searchTerm}
                        setValue={setSearchTerm}
                        onSubmit={() => {}}
                        placeholder="Rechercher par nom, prénom, email"
                        type="text"
                        name="search"
                        icon="search"
                      />
                    </>
                  }
                >
                <div className={styles.wrapper}>
                    {loading ? (
                        <div className={styles.loading}>
                            <div className="spinner-large" />
                        </div>
                    ) : filteredCandidates.length > 0 ? (
                        <Table 
                            items={filteredCandidates} 
                            title="Candidats pour cette offre" 
                            selectedTutors={selectedCandidates}
                            onSelectTutor={handleSelectCandidate}
                            onSelectAll={handleSelectAll}
                        />
                    ) : candidates.length > 0 ? (
                        <div className={styles.empty}>Aucun candidat ne correspond à votre recherche</div>
                    ) : (
                        <div className={styles.empty}>Aucun candidat trouvé pour cette offre.</div>
                    )}
                </div>

            </Card>
            <Panel 
                selectedTutors={selectedCandidates} 
                onClearSelection={handleClearSelection} 
            />
        </>
    );
};

export default CandidateList;
